import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs/operators';
import { ShareOwnership } from '../../../core/enum/share-ownership.enum';
import { PortfolioList } from '../../../core/models/portfolio/portfolio-list';
import { PortfolioListModel } from '../../../core/models/portfolio/portfolio-list.model';
import { PortfolioService } from '../../../core/services/api/portfolio.service';
import { ErrorService } from '../../../core/services/error.service';
import { PortfolioSharePopupComponent } from '../portfolio-share-popup/portfolio-share-popup.component';
import { ShareMarketStatus } from '../../../core/enum/mysec/share-market-status.enum';
import { ValuationBasisPopupComponent } from '../valuation-basis-popup/valuation-basis-popup.component';
import { DefaultLogo } from '../../../core/services/constant';

/** Mobile card tree node: a company (single or aggregate parent) plus its counter children. */
interface PortfolioTreeNode {
  node: PortfolioListModel;
  children: PortfolioListModel[];
}

/**
 * One company's worth of rendering data — the unit that pagination pages over
 * (FRC-859: 12 companies per page). `rows` is the flat desktop-table form (a single
 * row, or an aggregate parent row followed by its counter children); `tree` is the
 * nested mobile-card form. Keeping a company's counters inside one unit guarantees
 * they never split across a page boundary.
 */
interface CompanyUnit {
  rows: PortfolioListModel[];
  tree: PortfolioTreeNode;
}

/**
 * The API pages by holding, but we page by company client-side, so we ask for the
 * whole portfolio in one call. Retail ECF portfolios are well under this; if one ever
 * exceeded it the tail would be dropped — revisit with server-side company paging then.
 */
const FETCH_ALL_TAKE = 1000;

@Component({
    selector: 'app-my-shares',
    templateUrl: './my-shares.component.html',
    styleUrls: ['./my-shares.component.scss'],
    standalone: false
})
export class MySharesComponent implements OnInit, OnChanges {
  @Input() portfolioListModel: PortfolioListModel[];
  @Input() total: number;
  @Input() provisional: boolean;

  /** Companies per page. The API pages by holding, so paging is done client-side
   *  over the full portfolio here (the parents fetch everything up front); this is
   *  the number of company rows shown per page, not a fetch `take`. */
  @Input() pageSize = 12;

  /**
   * Active page (1-based). Paging is client-side over the full portfolio, so this is
   * the component's own state — a page change re-slices in memory (FRC-859).
   */
  @Input() currentPage = 1;

  /** Desktop rows: multi-counter companies collapsed into an aggregate row + counter children. */
  groupedPortfolio: PortfolioListModel[] = [];

  /** Mobile cards: same grouping as desktop but nested — an aggregate parent card holds its counter child cards. */
  groupedTree: PortfolioTreeNode[] = [];

  /** Desktop rows actually rendered: a collapsed company hides its counter children. */
  visibleGroupedPortfolio: PortfolioListModel[] = [];

  /** All companies for the current search filter — the full set pagination pages over. */
  private companyUnits: CompanyUnit[] = [];

  /** Total company count across all pages — drives the pager's `totalItems`. */
  companyCount = 0;

  /**
   * Company group keys (see companyKey) whose counter children are expanded on
   * the desktop table. Default is collapsed — a company is only in this set once
   * the user expands it — so the aggregate row acts as the summary (FRC-859).
   */
  private expandedCompanies = new Set<string>();

  DefaultLogo = DefaultLogo;

  portfolioList: PortfolioList[] = [];
  displayColumns: string[] = [
    'Company',
    'ShareQty',
    'PurchasedPrice',
    'MarketPrice',
    'Profit',
    'Action',
  ];
  isLoading: boolean = false;
  searchTerm: string = '';

  ShareOwnership = ShareOwnership;
  ShareMarketStatus = ShareMarketStatus;

  constructor(
    private errorService: ErrorService,
    private portfolioService: PortfolioService,
    private modalService: NgbModal,
  ) {}

  ngOnInit(): void {
    if (!this.portfolioListModel) {
      this.getPortfolio();
    } else {
      this.rebuildGrouped();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['portfolioListModel']) {
      this.rebuildGrouped();
    }
  }

  /**
   * Holdings filtered by the search term (company brand name, legal name or
   * counter code). Returns the full list when no term is entered. Filtering is
   * client-side over the currently loaded page.
   */
  get displayedPortfolio(): PortfolioListModel[] {
    const list = this.portfolioListModel ?? [];
    const term = this.searchTerm?.trim().toLowerCase();
    if (!term) {
      return list;
    }

    return list.filter(
      (item) =>
        item.company?.brandName?.toLowerCase().includes(term) ||
        item.company?.name?.toLowerCase().includes(term) ||
        item.tickerCode?.toLowerCase().includes(term),
    );
  }

  onSearch(term: string): void {
    this.searchTerm = term ?? '';
    // A new search redefines the company set — start from the first page.
    this.currentPage = 1;
    this.rebuildGrouped();
  }

  /**
   * Builds the desktop row list from the (filtered) holdings, grouping counters
   * that belong to the same company. A company with 2+ counters becomes an
   * aggregate parent row (summed totals) followed by its counter child rows;
   * single-counter companies render as one normal row.
   */
  rebuildGrouped(): void {
    const flat = this.displayedPortfolio;
    const groups = new Map<string, PortfolioListModel[]>();
    const order: string[] = [];

    flat.forEach((item, index) => {
      const key = this.companyKey(item, index);
      if (!groups.has(key)) {
        groups.set(key, []);
        order.push(key);
      }
      groups.get(key)?.push(item);
    });

    const units: CompanyUnit[] = [];
    for (const key of order) {
      const members = groups.get(key) ?? [];
      if (members.length <= 1) {
        const single: PortfolioListModel = {
          ...members[0],
          isAggregate: false,
          isCounterChild: false,
          isFirstOfCompany: true,
          companyGroupKey: key,
          // Mobile card default: collapsed, matching the desktop default (AC 3).
          // The source item never carries this UI-only field, so it must be set here.
          isCollapsed: true,
        };
        units.push({ rows: [single], tree: { node: single, children: [] } });
      } else {
        const aggregate = {
          ...this.buildAggregate(members),
          companyGroupKey: key,
          counterCount: members.length,
          // Mobile card default: collapsed, matching the desktop default (AC 3).
          isCollapsed: true,
        };
        const children = members.map((m, i) => ({
          ...m,
          isAggregate: false,
          isCounterChild: true,
          isFirstOfCompany: false,
          isJoinedToPrevious: i > 0,
          isJoinedToNext: i < members.length - 1,
          companyGroupKey: key,
        }));
        units.push({ rows: [aggregate, ...children], tree: { node: aggregate, children } });
      }
    }

    this.companyUnits = units;
    this.companyCount = units.length;

    // Keep the current page in range as the company count changes (search, reload).
    const pageCount = Math.max(1, Math.ceil(units.length / this.pageSize));
    this.currentPage = Math.min(Math.max(this.currentPage, 1), pageCount);

    this.applyPaging();
  }

  /**
   * Whether a company's counter children are shown on the desktop table. While a
   * search term is active every group is force-expanded so a matching counter is
   * always visible (FRC-859 AC#6); otherwise it reflects the user's toggle, which
   * defaults to collapsed.
   */
  isCompanyExpanded(key: string | undefined): boolean {
    if (this.searchTerm?.trim()) {
      return true;
    }
    return !!key && this.expandedCompanies.has(key);
  }

  /**
   * Toggle a company's counter children (aggregate rows only). No-op while
   * searching, since groups are force-expanded then.
   */
  toggleCompany(item: PortfolioListModel, event?: Event): void {
    event?.stopPropagation();
    const key = item?.companyGroupKey;
    if (!key || this.searchTerm?.trim()) {
      return;
    }
    if (this.expandedCompanies.has(key)) {
      this.expandedCompanies.delete(key);
    } else {
      this.expandedCompanies.add(key);
    }
    this.applyPaging();
  }

  /**
   * Recompute the current page of companies — desktop rows and mobile cards — from
   * the current page and expand/collapse state. While a search term is active every
   * matching company is shown (no paging) so a match is never hidden on another page;
   * the pager is hidden then. A collapsed company still hides its counter children.
   */
  private applyPaging(): void {
    const searching = !!this.searchTerm?.trim();
    const pageUnits = searching
      ? this.companyUnits
      : this.companyUnits.slice(
          (this.currentPage - 1) * this.pageSize,
          this.currentPage * this.pageSize,
        );

    this.groupedTree = pageUnits.map((unit) => unit.tree);
    this.groupedPortfolio = pageUnits.flatMap((unit) => unit.rows);
    this.visibleGroupedPortfolio = this.groupedPortfolio.filter(
      (row) => !row.isCounterChild || this.isCompanyExpanded(row.companyGroupKey),
    );
  }

  /** Comma-separated counter codes for an aggregate's children (mobile card subtitle). */
  counterList(children: PortfolioListModel[]): string {
    return children
      .map((child) => child?.tickerCode)
      .filter((code): code is string => !!code)
      .join(', ');
  }

  /**
   * SimpleCompanyInfo (the DTO backing `company` on this endpoint) does not include
   * `id` or `companyId` — `registrationNumber` is the only value reliably shared
   * across two counters of the same real company. Items with a blank
   * registrationNumber must not collide on a shared fallback key — that would
   * group unrelated companies into one aggregate row — so those fall back to
   * a per-row key instead, keeping them ungrouped.
   */
  private companyKey(item: PortfolioListModel, index: number): string {
    const registrationNumber = item?.company?.registrationNumber?.trim();
    return registrationNumber ? `reg:${registrationNumber}` : `row:${index}`;
  }

  private buildAggregate(members: PortfolioListModel[]): PortfolioListModel {
    const sum = (selector: (m: PortfolioListModel) => number | undefined) =>
      members.reduce((total, m) => total + (Number(selector(m)) || 0), 0);

    const purchasedAmount = sum((m) => m.portfolio?.purchasedAmount);
    const marketPrice = sum((m) => m.portfolio?.marketPrice);
    const unrealisedGainLoss = sum((m) => m.portfolio?.unrealisedGainLoss);
    const shareQuantity = sum((m) => m.portfolio?.shareQuantity);

    const realisedValues = members
      .map((m) => m.portfolio?.realisedGainLoss)
      .filter((v): v is number => v !== undefined && v !== null);
    const realisedGainLoss = realisedValues.length
      ? realisedValues.reduce((a, b) => a + b, 0)
      : undefined;

    // Only report a costBasis for the aggregate when at least one member has one
    // (WAC/costBasis backfill may not have run for every counter yet) — but once
    // any member has it, fall back to that member's purchasedAmount per member
    // before summing, so a mixed group doesn't silently drop the members without
    // costBasis from the total. Doing this on the field itself (rather than only
    // in a local percentage calc) keeps the aggregate's displayed Cost Basis,
    // unrealisedGainLossPercentage, and totalGainLossPercentage() — which reads
    // this same field — all consistent with each other.
    const hasAnyCostBasis = members.some(
      (m) => m.portfolio?.costBasis !== undefined && m.portfolio?.costBasis !== null,
    );
    const costBasis = hasAnyCostBasis
      ? sum((m) => m.portfolio?.costBasis ?? m.portfolio?.purchasedAmount)
      : undefined;

    const costBasisOrPurchasedAmount = costBasis ?? purchasedAmount;
    const unrealisedGainLossPercentage = costBasisOrPurchasedAmount
      ? Number(((unrealisedGainLoss / costBasisOrPurchasedAmount) * 100).toFixed(2))
      : 0;

    const returnObject: PortfolioListModel = {
      ...members[0],
      isAggregate: true,
      isCounterChild: false,
      isFirstOfCompany: true,
      portfolio: {
        ...members[0].portfolio,
        shareQuantity,
        purchasedAmount,
        costBasis,
        marketPrice,
        unrealisedGainLoss,
        unrealisedGainLossPercentage,
        realisedGainLoss,
      },
    };
    returnObject.newlyImported = members.some((m) => m.newlyImported);
    return returnObject;
  }

  /**
   * Whether the desktop gain/loss cell has a breakdown worth expanding. When a
   * holding has neither an unrealised nor a realised gain/loss there is nothing
   * to reveal, so the chevron is hidden.
   */
  hasGainBreakdown(item: PortfolioListModel): boolean {
    const portfolio = item?.portfolio;
    if (!portfolio) {
      return false;
    }

    const realised = portfolio.realisedGainLoss;
    const hasRealised = realised !== undefined && realised !== null && realised !== 0;
    return !!portfolio.unrealisedGainLoss || hasRealised;
  }

  /** Whether a holding reports a realised gain/loss (drives the breakdown row). */
  hasRealised(item: PortfolioListModel): boolean {
    const realised = item?.portfolio?.realisedGainLoss;
    return realised !== undefined && realised !== null;
  }

  /**
   * Headline gain/loss = unrealised + realised. With no realised data (current
   * API) this equals the unrealised figure, so the live page is unchanged.
   */
  totalGainLoss(item: PortfolioListModel): number {
    const portfolio = item?.portfolio;
    return (portfolio?.unrealisedGainLoss ?? 0) + (portfolio?.realisedGainLoss ?? 0);
  }

  /** Total gain/loss as a percentage of cost basis (falls back to purchasedAmount when costBasis is absent). */
  totalGainLossPercentage(item: PortfolioListModel): number {
    const cost = item?.portfolio?.costBasis ?? item?.portfolio?.purchasedAmount;
    if (!cost) {
      return 0;
    }
    return Number(((this.totalGainLoss(item) / cost) * 100).toFixed(2));
  }

  getPortfolio() {
    this.isLoading = true;

    this.portfolioService
      .getPortfolio(1, FETCH_ALL_TAKE)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (data) => {
          let portfolioList = data.data;

          portfolioList.forEach((item: PortfolioListModel) => {
            const costBasisOrPurchasedAmount =
              item.portfolio?.costBasis ?? item.portfolio?.purchasedAmount;
            item.portfolio.unrealisedGainLossPercentage = costBasisOrPurchasedAmount
              ? Number(
                  (
                    (item.portfolio?.unrealisedGainLoss / costBasisOrPurchasedAmount) *
                    100
                  ).toFixed(2),
                )
              : 0;
            item.isCollapsed = true;
          });

          this.portfolioListModel = portfolioList;
          this.total = data.total;
          this.rebuildGrouped();
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }

  /**
   * PstxPagination emits the target page number (1-based). Pagination is now
   * client-side over the full portfolio (12 companies per page — FRC-859), so a
   * page change just re-slices the already-loaded data; no refetch or delegation.
   */
  onPageChange(page: number): void {
    this.currentPage = page;
    this.applyPaging();
  }

  openShare(portfolioListModel: PortfolioListModel) {
    const popupOptions = {
      size: 'md',
      centered: true,
      keyboard: false,
    } as NgbModalOptions;

    const modalRef = this.modalService.open(PortfolioSharePopupComponent, popupOptions);
    modalRef.componentInstance.portfolioListModel = portfolioListModel;
    modalRef.result.then(
      () => {},
      () => {},
    );
  }

  openUrl(url: string) {
    window.open(url);
  }

  openValuationBasisPopup(portfolioListModel: PortfolioListModel) {
    const popupOptions = {
      size: 'md',
      centered: true,
      keyboard: false,
    } as NgbModalOptions;

    const modalRef = this.modalService.open(ValuationBasisPopupComponent, popupOptions);
    modalRef.componentInstance.portfolioListModel = portfolioListModel;
    modalRef.result.then(
      () => {},
      () => {},
    );
  }
}
